import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Plus, Copy, Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../api/client";
import { useAppData } from "../data/AppDataContext";
import { formatNumberInput, unformatNumberInput, sanitizePhoneInput } from "../data/format";
import { EQUIPMENT_OPTIONS, sanitizeEquipmentName, canonicalEquipment } from "../data/equipment";

const STEPS = ["Customer", "Equipment", "Payment", "Review"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyPlanForm = {
  equipment_name: "",
  total_price: "",
  date_purchased: "",
  duration_months: "",
  sales_person: "",
  amount_paid: "",
};

function digitCount(str) {
  return (str.match(/\d/g) || []).length;
}

function SpinIcon({ size = 14, color = "var(--blue-600)" }) {
  return <Loader2 size={size} color={color} style={{ animation: "pl-spin 0.8s linear infinite" }} />;
}

export default function NewPlan() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedCustomerId = searchParams.get("customerId") || "";

  const appData = useAppData();

  const salesPeople = Array.isArray(appData?.salesPeople) ? appData.salesPeople : [];

  const {
    customerDetails = {},
    addSalesPerson,
    addPlanToCustomer,
    addCustomerWithPlan,
    checkCustomerMatches
  } = appData || {};

  const [matchCheck, setMatchCheck] = useState(null);
  const [linkChoice, setLinkChoice] = useState(undefined);
  const [step, setStep] = useState(preselectedCustomerId ? 1 : 0);
  const [mode, setMode] = useState(preselectedCustomerId ? "existing" : "new");
  const [selectedCustomerId, setSelectedCustomerId] = useState(preselectedCustomerId);
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const [preselectedCustomer, setPreselectedCustomer] = useState(
    preselectedCustomerId ? customerDetails[preselectedCustomerId] || null : null
  );
  const [customerSearch, setCustomerSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name: "", phone: "", email: "" });
  const [planForm, setPlanForm] = useState(emptyPlanForm);
  const [addingStaff, setAddingStaff] = useState(false);
  const [newStaffName, setNewStaffName] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const autocompleteRef = useRef(null);
  const submittingRef = useRef(false);

  const selectedCustomer = pickedCustomer;

  // Fetch the preselected customer directly (it may not be in any preloaded list).
  useEffect(() => {
    if (!preselectedCustomerId) return;
    let cancelled = false;
    api
      .customer(preselectedCustomerId)
      .then((c) => {
        if (!cancelled) setPreselectedCustomer(c);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [preselectedCustomerId]);

  // Server-side, debounced customer search for the "existing customer" autocomplete.
  useEffect(() => {
    const q = customerSearch.trim();
    if (mode !== "existing" || q.length < 2 || selectedCustomerId) {
      setSuggestions([]);
      setSearchingCustomers(false);
      return;
    }
    let cancelled = false;
    setSearchingCustomers(true);
    const t = setTimeout(async () => {
      try {
        const data = await api.customers({ search: q, limit: 6 });
        if (!cancelled) setSuggestions(Array.isArray(data) ? data : data?.results || []);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSearchingCustomers(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [customerSearch, selectedCustomerId, mode]);

  useEffect(() => {
    function onClickOutside(e) {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  function pickCustomer(c) {
    setSelectedCustomerId(String(c.id));
    setPickedCustomer(c);
    setCustomerSearch(c.name);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function validateStep(s) {
    const errs = {};
    if (s === 0) {
      if (mode === "existing") {
        if (!selectedCustomerId) errs.customer = "Search and select a customer.";
      } else {
        if (!customerForm.name.trim()) errs.name = "Customer name is required.";
        if (customerForm.phone && digitCount(customerForm.phone) < 7) errs.phone = "Enter a valid phone number.";
        if (customerForm.email && !EMAIL_RE.test(customerForm.email)) errs.email = "Enter a valid email address.";
      }
    }
    if (s === 1) {
      const equipment = sanitizeEquipmentName(planForm.equipment_name, { final: true });
      if (!equipment) errs.equipment_name = "Enter or pick the equipment.";
      else if (equipment.length < 2) errs.equipment_name = "Equipment name is too short.";
      if (!planForm.sales_person) errs.sales_person = "Select a sales person.";
    }
    if (s === 2) {
      if (!planForm.total_price) errs.total_price = "Enter the total price.";
      if (!planForm.date_purchased) errs.date_purchased = "Select the purchase date.";
      if (planForm.duration_months && Number(planForm.duration_months) <= 0) {
        errs.duration_months = "Duration must be greater than 0.";
      }
    }
    return errs;
  }

  function goNext() {
    if (step === 1) commitEquipmentName();
    const errs = validateStep(step);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function goBack() {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  }

  function setEquipmentName(value) {
    setPlanForm((f) => ({ ...f, equipment_name: sanitizeEquipmentName(value) }));
    setErrors(({ equipment_name, ...rest }) => rest);
  }

  function commitEquipmentName() {
    setPlanForm((f) => ({
      ...f,
      equipment_name: canonicalEquipment(sanitizeEquipmentName(f.equipment_name, { final: true })),
    }));
  }

  function pickEquipment(name) {
    setPlanForm((f) => ({ ...f, equipment_name: name }));
    setErrors(({ equipment_name, ...rest }) => rest);
  }

  async function submitNewStaff() {
    if (!newStaffName.trim()) return;
    const person = await addSalesPerson(newStaffName.trim());
    setPlanForm((f) => ({ ...f, sales_person: person.id }));
    setNewStaffName("");
    setAddingStaff(false);
  }

  function updateCustomerField(field, value) {
    setCustomerForm((f) => ({ ...f, [field]: value }));
    setMatchCheck(null);
    setLinkChoice(undefined);
  }

  async function handleCreate() {
    if (submittingRef.current) return;

    if (mode === "new" && matchCheck === null) {
      setSubmitting(true);
      try {
        const matches = await checkCustomerMatches(customerForm.email, customerForm.phone);
        setMatchCheck({ matches });
        if (matches && matches.length > 0) {
          setSubmitting(false);
          return;
        }
      } catch (err) {
        setSubmitError(err.message || "Something went wrong checking for an existing account.");
        setSubmitting(false);
        return;
      }
    }

    if (mode === "new" && matchCheck?.matches?.length > 0 && linkChoice === undefined) {
      return;
    }

    submittingRef.current = true;
    setSubmitError("");
    setSubmitting(true);
    try {
      const cleanedPlanForm = {
        ...planForm,
        equipment_name: canonicalEquipment(sanitizeEquipmentName(planForm.equipment_name, { final: true })),
        total_price: unformatNumberInput(planForm.total_price),
        amount_paid: unformatNumberInput(planForm.amount_paid),
      };
      if (mode === "existing") {
        const data = await addPlanToCustomer(selectedCustomerId, cleanedPlanForm);
        setCreated({ customer: data.customer, plan: data.plan });
      } else {
        const linkUserId = linkChoice || null;
        const result = await addCustomerWithPlan(customerForm, cleanedPlanForm, linkUserId);
        setCreated({ customer: result.customer, plan: result.plan });
      }
    } catch (err) {
      setSubmitError(err.message || "Something went wrong.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function copyLink(link) {
    if (navigator.clipboard) navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  const salesPersonName = salesPeople.find((s) => String(s.id) === String(planForm.sales_person))?.name || "";

  if (created) {
    return (
      <div>
        <div className="pl-page-title">New plan</div>
        <div className="pl-generated" style={{ maxWidth: 460 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Plan created for {created.customer.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="pl-token">{created.customer.link}</span>
            <button className="pl-btn pl-btn-ghost" onClick={() => copyLink(created.customer.link)} type="button">
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <div className="pl-meta" style={{ marginTop: 8 }}>
            This is {created.customer.name}'s one link - it now shows every plan they have, including this new one.
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <Link to={`/customers/${created.customer.id}`} className="pl-btn pl-btn-confirm">
              View this customer
            </Link>
            <button
              className="pl-btn pl-btn-ghost"
              onClick={() => {
                setCreated(null);
                setStep(0);
                setMode("new");
                setSelectedCustomerId("");
                setPickedCustomer(null);
                setSuggestions([]);
                setCustomerSearch("");
                setCustomerForm({ name: "", phone: "", email: "" });
                setPlanForm(emptyPlanForm);
                setMatchCheck(null);
                setLinkChoice(undefined);
              }}
            >
              Create another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="pl-page-title">New plan</div>
      <div className="pl-page-sub">
        {preselectedCustomer ? `Adding a plan for ${preselectedCustomer.name}.` : "Create a plan for an existing customer, or add a brand-new customer."}
      </div>

      <div className="pl-panel" style={{ padding: 36 }}>
        <div className="pl-stepper">
          {STEPS.map((label, i) => (
            <div key={label} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : "0 0 auto" }}>
              <div className={`pl-step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}>
                <span className="pl-step-dot">{i < step ? <Check size={12} /> : i + 1}</span>
                {label}
              </div>
              {i < STEPS.length - 1 && <div className={`pl-step-connector ${i < step ? "done" : ""}`} />}
            </div>
          ))}
        </div>

        <div className="pl-form-wide">
          {/* Step 0: Customer */}
          {step === 0 && (
            <>
              {!preselectedCustomerId && (
                <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
                  <button className={`pl-btn ${mode === "existing" ? "pl-btn-confirm" : "pl-btn-ghost"}`} onClick={() => setMode("existing")} type="button">
                    Existing customer
                  </button>
                  <button className={`pl-btn ${mode === "new" ? "pl-btn-confirm" : "pl-btn-ghost"}`} onClick={() => setMode("new")} type="button">
                    New customer
                  </button>
                </div>
              )}

              {mode === "existing" ? (
                <div className="pl-field" ref={autocompleteRef} style={{ position: "relative", maxWidth: 480 }}>
                  <label>Customer</label>
                  {preselectedCustomerId ? (
                    <input value={preselectedCustomer?.name || "Loading..."} disabled />
                  ) : (
                    <>
                      <style>{`
                        @keyframes pl-spin {
                          from { transform: rotate(0deg); }
                          to { transform: rotate(360deg); }
                        }
                      `}</style>
                      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                        <input
                          value={customerSearch}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            setSelectedCustomerId("");
                            setPickedCustomer(null);
                            setShowSuggestions(true);
                          }}
                          onFocus={() => setShowSuggestions(true)}
                          placeholder="Type a name or phone number..."
                          style={{ width: "100%", paddingRight: searchingCustomers ? 34 : undefined }}
                        />
                        {searchingCustomers && (
                          <div style={{ position: "absolute", right: 10, display: "flex", alignItems: "center" }}>
                            <SpinIcon size={14} />
                          </div>
                        )}
                      </div>
                      {showSuggestions && suggestions.length > 0 && (
                        <div className="pl-autocomplete-list">
                          {suggestions.map((c) => (
                            <div key={c.id} className="pl-autocomplete-item" onClick={() => pickCustomer(c)}>
                              <div style={{ fontWeight: 700 }}>{c.name}</div>
                              <div className="pl-meta">{c.phone || c.email || "-"}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {showSuggestions && !searchingCustomers && !selectedCustomerId && customerSearch.trim().length >= 2 && suggestions.length === 0 && (
                        <div className="pl-meta" style={{ marginTop: 6 }}>No matching customers.</div>
                      )}
                      {errors.customer && <div className="pl-field-error">{errors.customer}</div>}
                      {selectedCustomer && (
                        <div className="pl-meta" style={{ marginTop: 6 }}>
                          Selected: <strong>{selectedCustomer.name}</strong>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="pl-field-grid pl-field-grid-3">
                  <div className="pl-field">
                    <label>Customer name</label>
                    <input value={customerForm.name} onChange={(e) => updateCustomerField("name", e.target.value)} placeholder="e.g. Blessing Ngozi" />
                    {errors.name && <div className="pl-field-error">{errors.name}</div>}
                  </div>
                  <div className="pl-field">
                    <label>Phone</label>
                    <input type="tel" value={customerForm.phone} onChange={(e) => updateCustomerField("phone", sanitizePhoneInput(e.target.value))} placeholder="e.g. 0803 555 1122" />
                    {errors.phone && <div className="pl-field-error">{errors.phone}</div>}
                  </div>
                  <div className="pl-field">
                    <label>Email</label>
                    <input type="email" value={customerForm.email} onChange={(e) => updateCustomerField("email", e.target.value)} placeholder="e.g. blessing@email.com" />
                    {errors.email && <div className="pl-field-error">{errors.email}</div>}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 1: Equipment */}
          {step === 1 && (
            <div className="pl-field-grid pl-field-grid-2">
              <div className="pl-field">
                <label>Equipment</label>
                <input
                  value={planForm.equipment_name}
                  onChange={(e) => setEquipmentName(e.target.value)}
                  onBlur={commitEquipmentName}
                  placeholder="Type the equipment name..."
                  autoComplete="off"
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {EQUIPMENT_OPTIONS.map((eq) => {
                    const active = planForm.equipment_name.trim().toLowerCase() === eq.toLowerCase();
                    return (
                      <button
                        key={eq}
                        type="button"
                        className={`pl-btn ${active ? "pl-btn-confirm" : "pl-btn-ghost"}`}
                        style={{ padding: "4px 10px", fontSize: 12 }}
                        onClick={() => pickEquipment(eq)}
                      >
                        {eq}
                      </button>
                    );
                  })}
                </div>
                {errors.equipment_name && <div className="pl-field-error">{errors.equipment_name}</div>}
              </div>

              <div className="pl-field">
                <label>Sales person</label>
                {addingStaff ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={newStaffName} onChange={(e) => setNewStaffName(e.target.value)} placeholder="New staff name" autoFocus />
                    <button type="button" className="pl-btn pl-btn-confirm" onClick={submitNewStaff}>
                      Add
                    </button>
                    <button type="button" className="pl-btn pl-btn-ghost" onClick={() => setAddingStaff(false)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <select value={planForm.sales_person} onChange={(e) => setPlanForm((f) => ({ ...f, sales_person: e.target.value }))} style={{ flex: 1 }}>
                      <option value="">Select sales person</option>
                      {salesPeople.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="pl-btn pl-btn-ghost" onClick={() => setAddingStaff(true)}>
                      <Plus size={13} /> New staff
                    </button>
                  </div>
                )}
                {errors.sales_person && <div className="pl-field-error">{errors.sales_person}</div>}
              </div>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="pl-field-grid pl-field-grid-2">
              <div className="pl-field">
                <label>Total price (naira)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={planForm.total_price}
                  onChange={(e) => setPlanForm((f) => ({ ...f, total_price: formatNumberInput(e.target.value) }))}
                  placeholder="e.g. 1,200,000"
                />
                {errors.total_price && <div className="pl-field-error">{errors.total_price}</div>}
              </div>
              <div className="pl-field">
                <label>Date purchased</label>
                <input
                  type="date"
                  max={new Date().toISOString().split("T")[0]}
                  value={planForm.date_purchased}
                  onChange={(e) => setPlanForm((f) => ({ ...f, date_purchased: e.target.value }))}
                />
                {errors.date_purchased && <div className="pl-field-error">{errors.date_purchased}</div>}
              </div>
              <div className="pl-field">
                <label>Duration (months)</label>
                <input type="number" value={planForm.duration_months} onChange={(e) => setPlanForm((f) => ({ ...f, duration_months: e.target.value }))} placeholder="e.g. 6" />
                {errors.duration_months && <div className="pl-field-error">{errors.duration_months}</div>}
              </div>
              <div className="pl-field">
                <label>Amount already paid (naira, optional)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={planForm.amount_paid}
                  onChange={(e) => setPlanForm((f) => ({ ...f, amount_paid: formatNumberInput(e.target.value) }))}
                  placeholder="0"
                />
                <div className="pl-meta" style={{ marginTop: 4 }}>
                  Creates a pending payment - still needs accountant confirmation like any other claim.
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="pl-review" style={{ maxWidth: 560 }}>
              <div className="pl-review-row">
                <span className="pl-review-label">Customer</span>
                <span>{mode === "existing" ? (preselectedCustomer?.name || selectedCustomer?.name) : customerForm.name}</span>
              </div>
              {mode === "new" && (
                <div className="pl-review-row">
                  <span className="pl-review-label">Contact</span>
                  <span>{[customerForm.phone, customerForm.email].filter(Boolean).join(" - ") || "-"}</span>
                </div>
              )}
              <div className="pl-review-row">
                <span className="pl-review-label">Equipment</span>
                <span>{planForm.equipment_name}</span>
              </div>
              <div className="pl-review-row">
                <span className="pl-review-label">Sales person</span>
                <span>{salesPersonName}</span>
              </div>
              <div className="pl-review-row">
                <span className="pl-review-label">Total price</span>
                <span>{"₦" + (planForm.total_price || "0")}</span>
              </div>
              <div className="pl-review-row">
                <span className="pl-review-label">Date purchased</span>
                <span>{planForm.date_purchased}</span>
              </div>
              <div className="pl-review-row">
                <span className="pl-review-label">Duration</span>
                <span>{planForm.duration_months ? `${planForm.duration_months} months` : "-"}</span>
              </div>
              <div className="pl-review-row">
                <span className="pl-review-label">Amount already paid</span>
                <span>{"₦" + (planForm.amount_paid || "0")}</span>
              </div>

              {mode === "new" && matchCheck?.matches?.length > 0 && linkChoice === undefined && (
                <div style={{
                  background: "var(--amber-50, #fffbeb)", border: "1px solid var(--amber-100, #fef3c7)",
                  borderRadius: 8, padding: "14px 16px", margin: "14px 0",
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                    We found {matchCheck.matches.length === 1 ? "an existing CORS account" : "existing CORS accounts"} that might be this person
                  </div>
                  {matchCheck.matches.map((m) => (
                    <div key={m.user_id} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: "var(--white)", border: "1px solid var(--line)", borderRadius: 6,
                      padding: "10px 12px", marginBottom: 8,
                    }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{m.full_name}</div>
                        <div className="pl-meta">{[m.username, m.email, m.phone].filter(Boolean).join(" · ")}</div>
                      </div>
                      <button
                        type="button"
                        className="pl-btn pl-btn-confirm"
                        onClick={() => setLinkChoice(m.user_id)}
                      >
                        Link to this account
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="pl-btn pl-btn-ghost"
                    onClick={() => setLinkChoice(null)}
                    style={{ marginTop: 4 }}
                  >
                    Not the same person — create as new customer
                  </button>
                </div>
              )}

              {mode === "new" && linkChoice && (
                <div className="pl-meta" style={{ marginTop: 8 }}>
                  Linking to existing account: <strong>{matchCheck?.matches?.find((m) => m.user_id === linkChoice)?.full_name}</strong>
                </div>
              )}

              {submitError && (
                <div style={{ background: "var(--red-50)", color: "var(--red-700)", border: "1px solid var(--red-100)", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, margin: "14px 0" }}>
                  {submitError}
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
            <button className="pl-btn pl-btn-ghost" onClick={goBack} disabled={step === 0} style={{ visibility: step === 0 ? "hidden" : "visible" }}>
              <ChevronLeft size={14} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button className="pl-btn pl-btn-confirm" onClick={goNext}>
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button
                className="pl-btn pl-btn-confirm"
                onClick={handleCreate}
                disabled={submitting || (mode === "new" && matchCheck?.matches?.length > 0 && linkChoice === undefined)}
              >
                <Plus size={14} />
                {submitting
                  ? (matchCheck === null ? "Checking..." : "Creating...")
                  : mode === "new" && matchCheck?.matches?.length > 0 && linkChoice === undefined
                    ? "Resolve match above first"
                    : "Create plan"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}